import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import VideoState from '../shared/stores/root-store/video.state';
import {selectForms, selectOverlays} from '../shared/stores/root-store/selectors/overlay.selector';
import OverlayModel from '../../models/overlay.model';
import FormModel from '../../models/form.model';
import {UntypedFormBuilder} from '@angular/forms';
import {SetImagesPreloadedAction} from '../shared/stores/root-store/actions/video.action';
import {VideoStateEnum} from '../../enums/video-state.enum';
import {selectActualVideoState, selectTotalTimeFromState} from '../shared/stores/root-store/selectors/video.selector';
import {BufferService} from '../shared/services/buffer.service';
import {SessionService} from '../shared/services/session.service';
import {VideoEffect} from '../shared/stores/root-store/effects/video.effect';
import {VideoService} from '../shared/services/video.service';
import _ from 'lodash';
import {OverlayService} from '../shared/services/overlay.service';
import {OverlayType} from '../../enums/overlay-type.enum';

@Component({
    selector: 'eocm-overlay',
    templateUrl: './overlay.component.html',
    styleUrls: ['./overlay.component.scss']
})
export class OverlayComponent implements OnInit {
    @Input() visible: boolean;
    overlays: Array<OverlayModel> = [];
    initialOverlays: Array<OverlayModel>;
    forms: Array<FormModel> = [];
    videoState: VideoStateEnum;
    totalVideoTime: number;

    readonly OverlayType = OverlayType;

    constructor(
        private store: Store<{ video: VideoState }>,
        private formBuilder: UntypedFormBuilder,
        private effects: VideoEffect,
        private bufferService: BufferService,
        private cdRef: ChangeDetectorRef,
        private sessionService: SessionService,
        private overlayService: OverlayService,
        private videoService: VideoService) {
    }

    ngOnInit(): void {
        this.store.select(selectActualVideoState).subscribe((state) => {
            this.videoState = state;
        });

        this.store.select(selectTotalTimeFromState).subscribe(totalTime => {
            this.totalVideoTime = totalTime;
        });

        this.store.select(selectOverlays).subscribe(async overlays => {
            this.initialOverlays = _.cloneDeep(overlays);
            this.overlays = overlays;

            this.initialOverlays.forEach(overlay => {
                this.overlayService.addTimeToLastSceneOverlay(overlay, this.totalVideoTime);
            });

            this.replaceTagsWithValues(this.overlays);
            this.store.dispatch(SetImagesPreloadedAction({
                imagesPreloaded: false
            }));
            await this.preloadAllImages();

            this.cdRef.detectChanges();
        });

        this.store.select(selectForms).subscribe(forms => {
            this.forms = forms;
            this.forms.forEach(form => {
                this.replaceTagsWithValues(form.fields);
            });
            this.cdRef.detectChanges();
        });

        this.sessionService.sessionUpdated.subscribe(() => {
            this.resetOverlays();
            this.replaceTagsWithValues(this.overlays);
        });

        this.videoService.replayButtonPressed.subscribe(() => {
            this.resetOverlays();
        });
    }

    overlayTrackBy(index: number, overlay: OverlayModel) {
        return overlay._id;
    }

    formTrackBy(index: number, form: FormModel) {
        return form.id;
    }

    private replaceTagsWithValues(overlays) {
        const data = new Map(this.sessionService.data);
        data.set('uid', this.sessionService.uid);

        overlays.forEach(overlay => {
            const personalizationPath = this.getPersonalizationPath(overlay);

            if (personalizationPath) {
                for (const mapElement of data.entries()) {
                    const key = new RegExp('{{' + mapElement[0] + '}}', 'g');

                    _.set(overlay, personalizationPath, (_.get(overlay, personalizationPath).replace(key, mapElement[1])));
                }
            }
        });
    }

    private getPersonalizationPath(overlay) {
        const isValidButton = overlay.navigateTo && overlay.navigateTo.target && overlay.navigateTo.type === 'url';
        const isValidInput = overlay.type === 'input' && overlay.value !== undefined;

        if (isValidButton) {
            return 'navigateTo.target';
        } else if (isValidInput) {
            return 'value';
        }
        return null;
    }

    stopVideo() {
        if (this.videoState !== VideoStateEnum.Ended && this.videoState !== VideoStateEnum.Interaction) {
            this.bufferService.stopVideo();
        }
    }

    private resetOverlays() {
        this.overlays = _.cloneDeep(this.initialOverlays);
    }

    private async preloadAllImages() {
        await Promise.all(
            this.overlays
                .filter(overlay => overlay.type === 'image')
                .sort((overlay1, overlay2) => overlay1.time.start - overlay2.time.start)
                .filter(this.uniqueImagesFilter)
                .map(overlay => this.preloadImage(overlay.image))
        );

        this.store.dispatch(SetImagesPreloadedAction({
            imagesPreloaded: true
        }));
    }

    private uniqueImagesFilter(overlay, index, arr) {
        return arr.findIndex(t => t.image === overlay.image) === index;
    }

    private preloadImage(src) {
        return new Promise(resolve => {
            const image = new Image();
            image.src = src;
            image.onload = resolve;
            image.onerror = (event: ErrorEvent) => {
                console.warn('Image preloading error', (event.target as HTMLImageElement).src);
                resolve(event);
            };

            if (src.includes('eocm-client-data')) {
                image.src = src.replace(/^(https?:\/\/)([^.]+)\.s3(?:\.[^.]+)?\.amazonaws\.com(\/.*)?$/,
                    '$1$2.s3-accelerate.amazonaws.com$3')
            }
        });
    }
}
